import {FlexPage} from '@hconnect/common/components/FlexPage'
import {EventHighlight} from '@hconnect/common/components/shiftEventFormFields/EventHighlight'
import {
  eventActionButtonSX,
  noSideCard,
  sideCardSize,
  tableWithOutSideCard,
  tableWithSideCard,
  withSideCard
} from '@hconnect/common/consts'
import {useKpiNodeIds} from '@hconnect/common/hooks'
import {useArrowKeys, ArrowKey, scrollToEvent} from '@hconnect/common/hooks/useArrowKeys'
import {useWidth} from '@hconnect/common/hooks/useWidth'
import {FilterContrastProvider} from '@hconnect/common/styles'
import {EventsTab, ShiftEvent, ShiftEventsSortBy, SortOrder} from '@hconnect/common/types'
import {
  generateMarkdownOptions,
  getHighlightedTitleWithLink
} from '@hconnect/common/utils/highlightHelpers'
import {
  PaginationOptions,
  IconWithLabelButton,
  CardBox,
  IconWithTooltipButton,
  MarkdownText,
  buttonCheckedSx,
  buttonSx
} from '@hconnect/uikit/src/lib2'
import {useUpmPlantNow} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Add as AddIcon, TextSnippetOutlined, Sort as SortIcon} from '@mui/icons-material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import ListIcon from '@mui/icons-material/List'
import {Button, Grid, Box, CircularProgress, Theme, Typography, FormControl} from '@mui/material'
import {desktopFilterOptions, defaultDesktop, FilterOptions} from '@widgets/shift-event-filters'
import {TFunction} from 'i18next'
import {isEqual} from 'lodash'
import {Moment} from 'moment-timezone'
import React, {useCallback, useMemo, useState, ComponentType} from 'react'
import {useTranslation} from 'react-i18next'

import {ShiftEventsFilterBar, ShiftEventsTabToggle} from '../components'
import {
  generateCategoryColumn,
  generateCommentsColumn,
  generateCreatedAtColumn,
  generateEquipmentNumberColumn,
  generateEventTypeColumn,
  generateMaintenanceNotificationColumn,
  generateMainEquipmentNumberColumn,
  generateStatusColumn,
  generateTitleColumn,
  generateUpmDepartmentIdColumn
} from '../components/tableHelpers'
import {FEATURE_NAMESPACE} from '../consts'
import {useInvertedSurface} from '../context/InvertedSurfaceContext'
import {EventsTable, SortingOptions} from '../layouts'
import {getActionContentSXWithFilter, getButtonOutlineWhite} from '../styles/common'
import {ColumnsMap} from '../types'
import {ViewMode} from '../types/viewMode.types'
import {modeAfterClose} from '../utils'

export type SideCardProps = {
  mode: 'createMode' | 'detailsMode' | 'editMode'
  itemId?: string
  initialData?: Partial<ShiftEvent>
  setMode: (mode: ViewMode) => void
  doClose: (original?: ShiftEvent, editVersion?: ShiftEvent) => void
  freeSearchText?: string
  plantId: string
  onCreationStart?: (uuid: string) => void
  onCreationFinish?: (uuid: string) => void
  onSapPmNotification?: (timezone: string) => void
}

export type EventsViewProps = {
  plantId: string
  activeFilter: FilterOptions
  events?: ShiftEvent[]
  isLoading?: boolean
  showBottomProgress?: boolean
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  onFilterChange: (next: FilterOptions) => void
  paginationOptions?: PaginationOptions
  isEventDownloadLoading: boolean
  onEventsDownload: () => Promise<void>
  activeTab: EventsTab
  setActiveTab: (tab: EventsTab) => void
  initialCreateData?: Partial<ShiftEvent>
  SideCard: ComponentType<SideCardProps>
  onCreationStart?: (uuid: string) => void
  onCreationFinish?: (uuid: string) => void
  onSapPmNotification?: (timezone: string) => void
}

export const generateColumnsMap = (
  t: TFunction,
  plantNow: Moment,
  freeSearchText?: string,
  kpis: Record<string, string> = {},
  shouldShowStoppedMainEquipment = false
) => {
  const tableSizeColumnsMap: ColumnsMap<ShiftEvent> = {
    XS: [
      generateTitleColumn({
        translation: t,
        type: 'withEquipment',
        markOverdueTask: true,
        freeSearchText,
        plantNow
      })
    ],
    S: [
      generateEventTypeColumn(t, 'small'),
      generateTitleColumn({
        translation: t,
        type: 'withEquipment',
        markOverdueTask: true,
        freeSearchText,
        plantNow
      }),
      generateStatusColumn(t, 'small')
    ],
    M: [
      generateEventTypeColumn(t, 'small'),
      generateTitleColumn({translation: t, freeSearchText, plantNow}),
      generateEquipmentNumberColumn(t),
      generateCategoryColumn(t),
      generateStatusColumn(t, 'small')
    ],
    L: [
      generateEventTypeColumn(t, 'small'),
      generateTitleColumn({translation: t, freeSearchText, plantNow}),
      generateCommentsColumn(),
      generateEquipmentNumberColumn(t),
      generateCategoryColumn(t),
      generateCreatedAtColumn(t),
      ...(shouldShowStoppedMainEquipment ? [generateUpmDepartmentIdColumn(t, kpis)] : []),
      generateStatusColumn(t, 'small')
    ],
    XL: [
      generateEventTypeColumn(t, 'regular'),
      generateTitleColumn({translation: t, freeSearchText, plantNow}),
      generateCommentsColumn(),
      generateEquipmentNumberColumn(t),
      generateMainEquipmentNumberColumn(t),
      generateCategoryColumn(t),
      generateCreatedAtColumn(t),
      generateMaintenanceNotificationColumn(t, freeSearchText),
      ...(shouldShowStoppedMainEquipment ? [generateUpmDepartmentIdColumn(t, kpis)] : []),
      generateStatusColumn(t, 'regular')
    ]
  }

  return tableSizeColumnsMap
}

const ShiftEventsTable: React.FC<{
  plantId: string
  events?: ShiftEvent[]
  isLoading?: boolean
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  showDescriptions: boolean
  paginationOptions?: PaginationOptions
  highlightedOnly?: boolean
  isSideCardShown: boolean
  freeSearchText?: string
  sortingOptions: SortingOptions
}> = ({
  plantId,
  events,
  highlightedOnly = false,
  setViewMode,
  viewMode,
  showDescriptions,
  paginationOptions,
  isLoading,
  isSideCardShown,
  freeSearchText,
  sortingOptions
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const departmentOptions = useKpiNodeIds(plantId)

  const departmentsById = useMemo(() => {
    return departmentOptions.reduce(
      (prev, curr) => ({...prev, [curr.id]: curr.label}),
      {} as Record<string, string>
    )
  }, [departmentOptions])
  const getPlantNow = useUpmPlantNow(plantId)
  const shouldShowStoppedMainEquipment = useCommonFeatureFlag(FeatureFlagKey.StoppedMainEquipment)
  const columns = useMemo<ColumnsMap<ShiftEvent>>(
    () =>
      generateColumnsMap(
        t,
        getPlantNow(),
        freeSearchText,
        departmentsById,
        !!shouldShowStoppedMainEquipment
      ),
    [t, getPlantNow, freeSearchText, departmentsById, shouldShowStoppedMainEquipment]
  )
  const breakPoint = useWidth()
  const shiftEvents = useMemo(
    () => (highlightedOnly ? events?.filter((ev) => ev.isHighlighted) : events),
    [highlightedOnly, events]
  )
  const tableSize = (isSideCardShown ? withSideCard : noSideCard).get(breakPoint) ?? 'S'

  const onRowClicked = useCallback(
    (clickedItem: ShiftEvent) => setViewMode({mode: 'detailsMode', itemId: clickedItem.id}),
    [setViewMode]
  )

  const selectedItemId =
    viewMode.mode === 'detailsMode' || viewMode.mode === 'editMode' ? viewMode.itemId : undefined

  const arrowKeyHandler = useCallback(
    (arrow: ArrowKey) => {
      if (
        !events ||
        !events.length ||
        viewMode.mode === 'editMode' ||
        viewMode.mode === 'createMode'
      ) {
        return
      }

      const currentPosition = !selectedItemId
        ? -1
        : events.findIndex((e) => e.id === selectedItemId)
      let newPosition = -1
      if (arrow === 'up') {
        if (currentPosition === -1) {
          newPosition = events.length - 1
        } else {
          newPosition = (currentPosition - 1 + events.length) % events.length
        }
      }
      if (arrow === 'down') {
        if (currentPosition === -1) {
          newPosition = 0
        } else {
          newPosition = (currentPosition + 1) % events.length
        }
      }
      scrollToEvent(arrow, newPosition)

      const newEventToBeSelected = events[newPosition]
      if (newEventToBeSelected) {
        onRowClicked(newEventToBeSelected)
      }
    },
    [events, selectedItemId, onRowClicked, viewMode.mode]
  )

  useArrowKeys(arrowKeyHandler)

  return (
    <EventsTable
      events={shiftEvents}
      onRowClick={onRowClicked}
      paginationOptions={paginationOptions}
      columns={columns[tableSize]}
      selectedItemId={selectedItemId}
      isLoading={isLoading}
      sortingOptions={sortingOptions}
      rowFooterTemplate={
        showDescriptions
          ? ({rowData}) => (
              <Typography
                component="div"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
                data-test-id={`DataTable-desc-${rowData.id}`}
              >
                {rowData.description && (
                  <MarkdownText options={generateMarkdownOptions(freeSearchText)}>
                    {freeSearchText
                      ? getHighlightedTitleWithLink(rowData.description, freeSearchText, true)
                      : rowData.description}
                  </MarkdownText>
                )}
              </Typography>
            )
          : undefined
      }
    />
  )
}

export const EventsView: React.FC<EventsViewProps> = (props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {viewMode, setViewMode, activeTab, setActiveTab, plantId, SideCard} = props
  const breakPoint = useWidth()
  const [showDescriptions, setShowDescriptions] = useState<boolean>(false)
  const {activeFilter, onFilterChange} = props
  const inverted = useInvertedSurface()

  const sortByLatest = useMemo(
    () => activeFilter.sortBy === ShiftEventsSortBy.LastModified,
    [activeFilter.sortBy]
  )

  const {sortBy, sortOrder} = activeFilter
  const [highlightedOnly, setHighlightedOnly] = useState<boolean | undefined>(false)

  const isSideCardShown = viewMode.mode !== 'tableOnlyMode'
  const isSmall = breakPoint === 'xs' || breakPoint === 'sm'
  const isTableShown = !isSideCardShown || !isSmall

  const [isMobileFilterOpen, openMobileFilter] = useState(false)
  const doClose = (original?: ShiftEvent, editVersion?: ShiftEvent) => {
    const nextMode: ViewMode = modeAfterClose(original, editVersion)
    setViewMode(nextMode)
  }

  const freeSearchText = activeFilter.freeText

  React.useEffect(() => {
    if (freeSearchText && !showDescriptions) {
      setShowDescriptions(true)
    }
  }, [freeSearchText, showDescriptions])

  const handleSortingChange = (sortBy?: ShiftEventsSortBy, sortOrder?: SortOrder) => {
    const next = {...activeFilter, sortBy, sortOrder}

    if (!sortBy || !sortOrder || isEqual(activeFilter, next)) {
      return
    }

    onFilterChange(next)
  }

  return (
    <FlexPage
      appName="Cockpit"
      title={
        isMobileFilterOpen ? (
          t('shiftEvent.action.filter')
        ) : (
          <FilterContrastProvider isContrastMode={inverted}>
            <ShiftEventsTabToggle
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              plantId={plantId}
            />
          </FilterContrastProvider>
        )
      }
      sxActionContent={getActionContentSXWithFilter(isMobileFilterOpen)}
      headerActionContent={
        <FilterContrastProvider isContrastMode={inverted}>
          <Grid container alignItems="center">
            {isTableShown && (
              <>
                <Grid item xs={12}>
                  <ShiftEventsFilterBar
                    showDescriptions={setShowDescriptions}
                    activeSettings={activeFilter}
                    isCollapsible={isSmall}
                    onChange={onFilterChange}
                    openMobileFilter={openMobileFilter}
                    isMobileFilterOpen={isMobileFilterOpen}
                    plantId={plantId}
                    options={desktopFilterOptions}
                    defaultFilters={defaultDesktop}
                    additionalActions={
                      !isSmall && (
                        <IconWithLabelButton
                          disabled={props.isEventDownloadLoading}
                          onClick={props.onEventsDownload}
                          sx={(theme: Theme) => ({
                            margin: theme.spacing(1, 1.25),
                            ...getButtonOutlineWhite(theme),
                            '&.MuiButton-root:first-of-type': {
                              margin: theme.spacing(1, 1.25)
                            }
                          })}
                          data-test-id="events-download"
                          startIcon={<FileDownloadIcon />}
                          label={t('shiftEvent.action.export')}
                        />
                      )
                    }
                  />
                </Grid>
              </>
            )}
          </Grid>
        </FilterContrastProvider>
      }
      useViewportHeight={!isSmall}
    >
      {!isMobileFilterOpen && (
        <Grid container flexGrow={1} overflow={'hidden'} spacing={2}>
          {isTableShown && (
            <Grid
              item
              height={'100%'}
              {...(isSideCardShown ? tableWithSideCard : tableWithOutSideCard)}
            >
              <CardBox
                sx={(theme: Theme) => ({
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  [theme.breakpoints.between('md', 'xl')]: {
                    zoom: 0.8
                  }
                })}
              >
                {isSmall ? (
                  <Box display="flex" justifyContent="space-around" mb={2}>
                    <Button
                      data-test-id="event-create"
                      onClick={() => {
                        setViewMode({mode: 'createMode'})
                      }}
                      color="secondary"
                    >
                      <AddIcon />
                    </Button>
                    <FormControl>
                      <IconWithTooltipButton
                        data-test-id="events-show-descriptions-toggle"
                        onClick={() => setShowDescriptions(!showDescriptions)}
                        icon={<ListIcon />}
                        title={t('eventsTable.action.toggleDescriptions')}
                        checked={showDescriptions}
                      />
                    </FormControl>
                    <FormControl>
                      <EventHighlight
                        data-test-id="events-highlighted-filter-toggle"
                        value={highlightedOnly}
                        onChange={() => setHighlightedOnly(!highlightedOnly)}
                        title={t('eventsTable.action.toggleHighLightedOnly')}
                      />
                    </FormControl>
                    <FormControl>
                      <IconWithTooltipButton
                        data-test-id="events-sort-by-latest-toggle"
                        onClick={() =>
                          handleSortingChange(
                            !sortByLatest
                              ? ShiftEventsSortBy.LastModified
                              : ShiftEventsSortBy.Created,
                            SortOrder.Descending
                          )
                        }
                        icon={<SortIcon />}
                        title={t('eventsTable.action.showLatestEvents')}
                        checked={sortByLatest}
                      />
                    </FormControl>
                  </Box>
                ) : (
                  <Box display={'flex'} justifyContent={'space-between'} mb={3}>
                    <Button
                      variant="text"
                      data-test-id="event-create"
                      onClick={() => {
                        setViewMode({mode: 'createMode'})
                      }}
                      startIcon={<AddIcon />}
                    >
                      {t('eventsTable.action.createEvent')}
                    </Button>

                    <Box
                      display={'flex'}
                      flexDirection={{xs: 'column', sm: 'row'}}
                      justifyContent={'flex-end'}
                      gap={1.5}
                    >
                      <FormControl>
                        <IconWithLabelButton
                          startIcon={<TextSnippetOutlined />}
                          onClick={() => setShowDescriptions(!showDescriptions)}
                          data-test-id="events-show-descriptions-toggle"
                          label={t('eventsTable.action.toggleDescriptions')}
                          variant="outlined"
                          sx={(theme) => ({
                            ...(showDescriptions ? buttonCheckedSx(theme) : buttonSx(theme)),
                            ...eventActionButtonSX
                          })}
                        />
                      </FormControl>

                      <FormControl>
                        <EventHighlight
                          data-test-id="events-highlighted-filter-toggle"
                          value={highlightedOnly}
                          onChange={() => setHighlightedOnly(!highlightedOnly)}
                          title={t('eventsTable.action.toggleHighLightedOnly')}
                          withLabel={true}
                        />
                      </FormControl>
                      <FormControl>
                        <IconWithLabelButton
                          startIcon={<SortIcon />}
                          onClick={() =>
                            handleSortingChange(
                              !sortByLatest
                                ? ShiftEventsSortBy.LastModified
                                : ShiftEventsSortBy.Created,
                              SortOrder.Descending
                            )
                          }
                          data-test-id="events-sort-by-latest-toggle"
                          label={t('eventsTable.action.showLatestEvents')}
                          variant="outlined"
                          sx={(theme) => ({
                            ...(sortByLatest ? buttonCheckedSx(theme) : buttonSx(theme)),
                            ...eventActionButtonSX
                          })}
                        />
                      </FormControl>
                    </Box>
                  </Box>
                )}

                <ShiftEventsTable
                  plantId={props.plantId}
                  events={props.events}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                  isLoading={props.isLoading}
                  showDescriptions={showDescriptions}
                  paginationOptions={props.paginationOptions}
                  isSideCardShown={isSideCardShown}
                  highlightedOnly={highlightedOnly}
                  freeSearchText={freeSearchText}
                  sortingOptions={{
                    sortBy,
                    sortOrder,
                    applySort: (sortBy?: ShiftEventsSortBy, sortOrder?: SortOrder) =>
                      handleSortingChange(sortBy, sortOrder)
                  }}
                />
                {props.showBottomProgress && (
                  <CircularProgress
                    sx={{marginX: 'auto', marginTop: 2}}
                    data-test-id="events-view-bottom-progress"
                  />
                )}
              </CardBox>
            </Grid>
          )}
          {isSideCardShown && (viewMode.mode as string) !== 'tableOnlyMode' && (
            <Grid
              item
              {...sideCardSize}
              sx={{
                height: '100%',
                overflow: 'hidden'
              }}
            >
              <SideCard
                {...viewMode}
                initialData={props.initialCreateData}
                setMode={setViewMode}
                doClose={doClose}
                freeSearchText={freeSearchText}
                plantId={plantId}
                onCreationStart={props.onCreationStart}
                onCreationFinish={props.onCreationFinish}
                onSapPmNotification={props.onSapPmNotification}
              />
            </Grid>
          )}
        </Grid>
      )}

      <p data-test-id="page-event-mode-indicator" style={{display: 'none'}}>
        {/* shall be used in cypress tests to validate the mode this page is in */}
        {viewMode.mode}
      </p>
    </FlexPage>
  )
}
